import { jsPDF } from "jspdf";
import { canv } from "@/canv/global";
import { getViewport, sortByIndexByParent } from "@/canvasController/helpers";
import type { Group } from "@/group/group";
import { selectionManager } from "@/tools/cursorTool";
import {
    getDocSize,
    mapGroupsWithoutFrameToFrames,
    queryAreaForExport,
    sortFramesByPosition,
} from "./utils";

export class BaseExport {
    async exportPage(groupsWithFrame: Group[], groupsWithoutFrame: Group[]) {
        const savedTransformation = {
            "0": canv.m[0],
            "3": canv.m[3],
            "4": canv.m[4],
            "5": canv.m[5],
        };

        const selectedGroups = canv.canvasController.getSelected();
        selectionManager.clearSelected();

        canv.zoomToSelected(groupsWithFrame, true);
        const areaWithFrames = canv.canvasController.getSelectedProps(groupsWithFrame);

        const newCanvas = document.createElement("canvas");
        newCanvas.classList.add("exportCanvas");
        document.body.prepend(newCanvas);

        const visualWidth = (areaWithFrames.width / canv.dpr) * canv.m[0];
        const visualHeight = (areaWithFrames.height / canv.dpr) * canv.m[0];

        newCanvas.style.width = `${visualWidth}px`;
        newCanvas.style.height = `${visualHeight}px`;

        const rect = newCanvas.getBoundingClientRect();
        newCanvas.width = rect.width * canv.dpr;
        newCanvas.height = rect.height * canv.dpr;

        const newCtx = newCanvas.getContext("2d");
        if (!newCtx) return;

        const appCtx = canv.ctx;
        canv.ctx = newCtx;

        canv.ctx.setTransform(
            canv.m[0],
            0,
            0,
            canv.m[3],
            -areaWithFrames.startX * canv.m[0],
            -areaWithFrames.startY * canv.m[0],
        );

        // Получаем фигуры на фреймах
        const groupsByFrames = mapGroupsWithoutFrameToFrames(groupsWithFrame, groupsWithoutFrame);
        const groupsForExport: Group[] = Object.values(groupsByFrames).reduce(
            (acc, cur) => {
                acc.push(...cur);
                return acc;
            },
            [...groupsWithFrame],
        );

        sortByIndexByParent(groupsForExport);

        canv.redraw();
        canv.redraw(true, true, groupsForExport);

        try {
            const doc = new jsPDF();
            await this.drawToDoc(newCanvas, visualWidth, visualHeight, doc);
            doc.save(`${canv.getInitDataProp("boardName")}.pdf`);
        } finally {
            newCanvas.remove();
            canv.ctx = appCtx;
            selectionManager.setSelected(selectedGroups);

            canv.m[0] = savedTransformation["0"];
            canv.m[3] = savedTransformation["3"];
            canv.m[4] = savedTransformation["4"];
            canv.m[5] = savedTransformation["5"];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        }
    }

    async exportPages(groupsWithFrame: Group[], groupsWithoutFrame: Group[]) {
        const savedTransformation = {
            "0": canv.m[0],
            "3": canv.m[3],
            "4": canv.m[4],
            "5": canv.m[5],
        };

        const selectedGroups = canv.canvasController.getSelected();
        selectionManager.clearSelected();

        const groupsByFrames = mapGroupsWithoutFrameToFrames(groupsWithFrame, groupsWithoutFrame);
        sortFramesByPosition(groupsWithFrame);

        const doc = new jsPDF();
        const appCtx = canv.ctx;
        const newCanvas = document.createElement("canvas");
        const newCtx = newCanvas.getContext("2d");
        if (!newCtx) return;

        document.body.prepend(newCanvas);

        let isFirstPage = true;

        try {
            for (const frameGroup of groupsWithFrame) {
                canv.zoomToSelected([frameGroup], true);

                const visualWidth = (frameGroup.width / canv.dpr) * canv.m[0];
                const visualHeight = (frameGroup.height / canv.dpr) * canv.m[0];

                newCanvas.style.width = `${visualWidth}px`;
                newCanvas.style.height = `${visualHeight}px`;

                const rect = newCanvas.getBoundingClientRect();
                newCanvas.width = rect.width * canv.dpr;
                newCanvas.height = rect.height * canv.dpr;

                canv.ctx = newCtx;

                canv.ctx.setTransform(
                    canv.m[0],
                    0,
                    0,
                    canv.m[3],
                    -frameGroup.startX * canv.m[0],
                    -frameGroup.startY * canv.m[0],
                );

                const groupsOnFrame = groupsByFrames[frameGroup.UID] || [];
                const groupsForExport = [frameGroup].concat(groupsOnFrame);
                sortByIndexByParent(groupsForExport);

                canv.redraw();
                canv.redraw(true, true, groupsForExport);

                await this.drawToDoc(newCanvas, visualWidth, visualHeight, doc, isFirstPage);
                this.clearCanvas(newCanvas, canv.ctx);

                isFirstPage = false;
            }

            doc.save(`${canv.getInitDataProp("boardName")}.pdf`);
        } finally {
            canv.ctx = appCtx;
            selectionManager.setSelected(selectedGroups);

            canv.m[0] = savedTransformation["0"];
            canv.m[3] = savedTransformation["3"];
            canv.m[4] = savedTransformation["4"];
            canv.m[5] = savedTransformation["5"];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        }
    }

    async exportViewport() {
        const savedTransformation = {
            "0": canv.m[0],
            "3": canv.m[3],
            "4": canv.m[4],
            "5": canv.m[5],
        };

        const selectedGroups = canv.canvasController.getSelected();
        selectionManager.clearSelected();

        const newCanvas = document.createElement("canvas");
        newCanvas.classList.add("exportCanvas");
        document.body.prepend(newCanvas);

        const viewport = getViewport();

        const visualWidth = (viewport.viewportWidth / canv.dpr) * canv.m[0];
        const visualHeight = (viewport.viewportHeight / canv.dpr) * canv.m[0];

        newCanvas.style.width = `${visualWidth}px`;
        newCanvas.style.height = `${visualHeight}px`;

        const rect = newCanvas.getBoundingClientRect();
        newCanvas.width = rect.width * canv.dpr;
        newCanvas.height = rect.height * canv.dpr;

        const newCtx = newCanvas.getContext("2d");
        if (!newCtx) return;

        const appCtx = canv.ctx;
        canv.ctx = newCtx;

        canv.ctx.setTransform(
            canv.m[0],
            0,
            0,
            canv.m[3],
            -viewport.viewportStartX * canv.m[0],
            -viewport.viewportStartY * canv.m[0],
        );

        const groupsForExport =
            queryAreaForExport({
                startX: viewport.viewportStartX,
                startY: viewport.viewportStartY,
                width: viewport.viewportWidth,
                height: viewport.viewportHeight,
            }) ?? [];

        sortByIndexByParent(groupsForExport);

        canv.redraw();
        canv.redraw(true, true, groupsForExport);

        try {
            const doc = new jsPDF();
            await this.drawToDoc(newCanvas, visualWidth, visualHeight, doc);
            doc.save(`${canv.getInitDataProp("boardName")}.pdf`);
        } finally {
            newCanvas.remove();
            canv.ctx = appCtx;
            selectionManager.setSelected(selectedGroups);

            canv.m[0] = savedTransformation["0"];
            canv.m[3] = savedTransformation["3"];
            canv.m[4] = savedTransformation["4"];
            canv.m[5] = savedTransformation["5"];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        }
    }

    private async drawToDoc(
        newCanvas: HTMLCanvasElement,
        visualWidth: number,
        visualHeight: number,
        doc: jsPDF,
        isFirstPage = true,
    ) {
        const snapshot = await new Promise((resolve: BlobCallback) =>
            newCanvas.toBlob(resolve, "image/jpeg", 1),
        );
        if (!snapshot) return;

        const blobUrl = URL.createObjectURL(snapshot);
        const { x, y, width, height } = getDocSize(doc, visualWidth, visualHeight);

        if (!isFirstPage) doc.addPage();

        doc.addImage(blobUrl, "JPEG", x, y, width, height);
    }

    private clearCanvas = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
    };
}
